import { Combobox } from "@headlessui/react";
import cx from "classnames";
import { useState } from "react";
import { Coordinates, Typography } from "@/components/atoms";
import { Place, usePlaces } from "@/hooks/usePlaces";

export interface LocationInputProps {
  className?: string;
  placeholder?: string;
  renderIcon: () => JSX.Element;
  onLocationChange: (v: Location | null) => void;
  hasError?: boolean;
  defaultValue?: string;
  allowManualInput?: boolean; // Allow user to submit manual address without Google validation
}

export type Location = {
  id: string;
  address: string;
  coordinates: Coordinates;
};

export const LocationInput = ({
  className,
  placeholder = "Search Location",
  onLocationChange,
  renderIcon,
  hasError = false,
  defaultValue,
  allowManualInput = false,
}: LocationInputProps) => {
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [manualAddress, setManualAddress] = useState<string>(defaultValue || "");
  const { getPredictions, predictions, placesService } = usePlaces();

  const handleOnPlaceChange = (place: Place | null) => {
    setSelectedPlace(place);
    if (place && placesService) {
      placesService.getDetails({ placeId: place.place_id, fields: ["geometry.location"] }, (details) => {
        if (details?.geometry?.location) {
          const coordinates = {
            lat: details.geometry.location.lat(),
            lng: details.geometry.location.lng(),
          };

          onLocationChange({
            id: place.place_id,
            address: place.description,
            coordinates,
          });
        } else {
          // Only set to null if not using manual input
          if (!allowManualInput) {
            onLocationChange(null);
          }
        }
      });
    } else {
      // Only set to null if not using manual input
      // When allowManualInput is true, we keep the manually typed value
      if (!allowManualInput) {
        onLocationChange(null);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setManualAddress(value);
    getPredictions(value);

    // If allowManualInput is enabled, update the location with manual address
    if (allowManualInput) {
      if (value) {
        onLocationChange({
          id: `manual-${Date.now()}`,
          address: value,
          coordinates: { lat: 0, lng: 0 }, // No coordinates for manual input
        });
      } else {
        onLocationChange(null);
      }
    }
  };

  return (
    <Combobox as="div" value={selectedPlace} onChange={handleOnPlaceChange} nullable>
      <div className={cx("relative", className)}>
        <Combobox.Input
          className={cx(
            "h-12 w-full min-w-[320px] rounded-lg border-neutral-mid-gray pl-10 text-base text-neutral-black placeholder-neutral-silver-gray focus:border-primary focus:shadow-input focus:ring-0",
            { "border-1 !focus:border-danger !border-danger": hasError }
          )}
          onChange={handleInputChange}
          displayValue={(p: Place) => (p ? p.description : manualAddress)}
          placeholder={placeholder}
          autoComplete="off"
        />
        <Combobox.Button className="absolute inset-y-0 left-0 flex items-center px-3 focus:outline-none">{renderIcon()}</Combobox.Button>

        {predictions.length > 0 && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md  bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {predictions.map((p) => (
              <Combobox.Option
                key={p.place_id}
                value={p}
                className="flex cursor-pointer gap-2 text-ellipsis px-2 py-2 hover:bg-primary-light"
              >
                <div className="flex w-[30px] flex-shrink-0 items-center justify-center">{renderIcon()}</div>
                <Typography>{p.description}</Typography>
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};
